import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { UnitSelect, rid } from "@/components/UnitSelect";
import { InvoicePrint } from "@/components/InvoicePrint";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Printer, Pencil, UserPlus, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

export default function SalesInvoices() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [salesStock, setSalesStock] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [salesId, setSalesId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [payment, setPayment] = useState("Tunai");
  const [dueDate, setDueDate] = useState("");
  const [trxDate, setTrxDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([{ _k: rid(), product_id: "", qty: 1, discount: 0, unit: "", sell_price: 0 }]);
  const [editing, setEditing] = useState(null);
  const [printInv, setPrintInv] = useState(null);
  const [payInv, setPayInv] = useState(null);
  const [newCust, setNewCust] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [flt, setFlt] = useState({ q: "", from: "", to: "", sales: "", pay: "", method: "" });
  const [ncName, setNcName] = useState("");
  const [ncPhone, setNcPhone] = useState("");
  const [ncCode, setNcCode] = useState("");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (flt.q) p.set("q", flt.q);
    if (flt.from) p.set("date_from", flt.from);
    if (flt.to) p.set("date_to", flt.to);
    if (flt.sales) p.set("sales_id", flt.sales);
    if (flt.pay) p.set("payment_status", flt.pay);
    if (flt.method) p.set("payment_method", flt.method);
    const [a, b, c, d] = await Promise.all([api.get(`/invoices?${p.toString()}`), api.get("/products"), api.get("/sales-agents"), api.get("/customers")]);
    setRows(a.data); setProducts(b.data); setSalesList(c.data); setCustomers(d.data);
  }, [flt]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!salesId) { setSalesStock([]); return; }
    api.get(`/sales-agents/${salesId}/stock`).then(r => setSalesStock(r.data));
  }, [salesId]);

  const priceCode = newCust ? ncCode.toUpperCase() : (customers.find(c => c.customer_id === customerId)?.price_code || "");
  const priceFor = (p, code, unit) => {
    const tiers = p?.price_tiers || {};
    const t = code ? tiers[code] : null;
    const base = p?.unit || "PCS";
    if (!t) return p?.sell_price || 0;
    const price = typeof t === "object" ? Number(t.price) : Number(t);
    const tUnit = (typeof t === "object" && t.unit) || base;
    const rowUnit = unit || base;
    if (rowUnit === tUnit) return price;
    const factorOf = (u) => p?.packs?.find(pk => pk.unit === u)?.factor || 1;
    if (rowUnit === base) return Math.round(price / factorOf(tUnit));
    return price * factorOf(rowUnit);
  };

  const resetForm = () => {
    setEditing(null); setSalesId(""); setCustomerId(""); setPayment("Tunai"); setDueDate("");
    setTrxDate(new Date().toISOString().slice(0, 10));
    setNewCust(false); setNcName(""); setNcPhone(""); setNcCode("");
    setItems([{ _k: rid(), product_id: "", qty: 1, discount: 0, unit: "", sell_price: 0 }]);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setSalesId(inv.sales_id);
    setCustomerId(inv.customer_id || "");
    setNewCust(false);
    setPayment(inv.payment_method);
    setDueDate(inv.due_date ? String(inv.due_date).slice(0, 10) : "");
    setItems(inv.items.map(it => ({ _k: rid(), product_id: it.product_id, qty: it.qty, discount: it.discount, unit: it.unit, sell_price: it.sell_price })));
    setOpen(true);
  };

  const openPrint = async (inv) => {
    try { setPrintInv((await api.get(`/invoices/${inv.invoice_id}`)).data); }
    catch { toast.error("Gagal memuat invoice"); }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Hapus invoice ${inv.trx_no}? Stok armada akan dikembalikan.`)) return;
    try { await api.delete(`/invoices/${inv.invoice_id}`); toast.success("Invoice dihapus — stok armada dikembalikan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const addItem = () => setItems([...items, { _k: rid(), product_id: "", qty: 1, discount: 0, unit: "", sell_price: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => {
    const n = [...items];
    n[i] = { ...n[i], [k]: v };
    if (k === "product_id") {
      const p = products.find(p => p.product_id === v);
      if (p) { n[i].unit = p.unit; n[i].sell_price = priceFor(p, priceCode, p.unit); }
    }
    if (k === "unit") {
      const p = products.find(p => p.product_id === n[i].product_id);
      if (p) n[i].sell_price = priceFor(p, priceCode, v);
    }
    setItems(n);
  };
  const onCustomer = (v) => {
    setCustomerId(v);
    const code = customers.find(c => c.customer_id === v)?.price_code || "";
    setItems(items.map(it => {
      if (!it.product_id) return it;
      const p = products.find(p => p.product_id === it.product_id);
      return { ...it, sell_price: priceFor(p, code, it.unit) };
    }));
  };
  const total = items.reduce((a, it) => a + Number(it.qty || 0) * Number(it.sell_price || 0) - Number(it.discount || 0), 0);

  const save = async () => {
    try {
      const cust = customers.find(c => c.customer_id === customerId);
      const payload = {
        sales_id: salesId,
        customer_id: newCust ? null : (customerId || null),
        customer: cust?.name || "Umum",
        new_customer_name: newCust ? ncName : undefined,
        new_customer_phone: newCust ? ncPhone : undefined,
        new_customer_price_code: newCust ? ncCode : undefined,
        payment_method: payment, due_date: dueDate || null,
        date: trxDate ? `${trxDate}T00:00:00+00:00` : undefined,
        items: items.filter(i => i.product_id && i.qty > 0),
      };
      if (editing) {
        await api.put(`/invoices/${editing.invoice_id}`, payload);
        toast.success("Invoice diperbarui");
      } else {
        await api.post("/invoices", payload);
        toast.success("Penjualan tersimpan");
      }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="page-sales" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Transaksi</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Penjualan</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-invoice" onClick={resetForm}><Plus className="h-4 w-4 mr-1" />Buat Invoice</Button></DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>{editing ? `Ubah Invoice ${editing.trx_no}` : "Invoice Penjualan"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Sales</Label>
                <Select value={salesId} onValueChange={setSalesId} disabled={!!editing}>
                  <SelectTrigger data-testid="input-sales"><SelectValue placeholder="Pilih sales" /></SelectTrigger>
                  <SelectContent>{salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.code} · {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pelanggan {priceCode && <Badge className="ml-1 bg-indigo-100 text-indigo-800 border-indigo-300 font-mono font-normal" data-testid="invoice-price-code">Kode {priceCode}</Badge>}</Label>
                {newCust ? (
                  <div className="space-y-1.5">
                    <Input placeholder="Nama pelanggan baru" value={ncName} onChange={(e) => setNcName(e.target.value)} data-testid="input-new-customer-name" />
                    <div className="flex gap-1.5">
                      <Input placeholder="No. HP" value={ncPhone} onChange={(e) => setNcPhone(e.target.value)} className="flex-1" />
                      <Input placeholder="Kode harga" value={ncCode} onChange={(e) => setNcCode(e.target.value.toUpperCase())} className="w-24" data-testid="input-new-customer-code" />
                    </div>
                    <Button type="button" variant="link" size="sm" className="h-5 px-0 text-xs" onClick={() => setNewCust(false)} data-testid="btn-cancel-new-customer">← Pilih dari daftar</Button>
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    <Popover open={custOpen} onOpenChange={setCustOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" role="combobox" data-testid="input-customer" className="w-full justify-between font-normal">
                          <span className="truncate">{customerId ? `${customers.find(c => c.customer_id === customerId)?.code} · ${customers.find(c => c.customer_id === customerId)?.name}` : "Umum / pilih pelanggan"}</span>
                          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Cari pelanggan…" data-testid="input-customer-search" />
                          <CommandList>
                            <CommandEmpty>Pelanggan tidak ditemukan</CommandEmpty>
                            <CommandGroup>
                              <CommandItem value="__umum" onSelect={() => { onCustomer(""); setCustOpen(false); }} data-testid="customer-opt-umum">Umum (tanpa pelanggan)</CommandItem>
                              {[...customers].sort((a, b) => a.name.localeCompare(b.name, "id")).map(c => (
                                <CommandItem key={c.customer_id} value={`${c.code} ${c.name}`} onSelect={() => { onCustomer(c.customer_id); setCustOpen(false); }} data-testid={`customer-opt-${c.customer_id}`}>
                                  {c.code} · {c.name}{c.price_code ? ` (${c.price_code})` : ""}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button type="button" variant="outline" size="icon" onClick={() => { setNewCust(true); setCustomerId(""); }} data-testid="btn-new-customer-inline" title="Pelanggan baru"><UserPlus className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
              <div>
                <Label>Pembayaran</Label>
                <Select value={payment} onValueChange={setPayment}>
                  <SelectTrigger data-testid="input-payment"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Tunai">Tunai</SelectItem><SelectItem value="Transfer">Transfer</SelectItem><SelectItem value="Tempo">Tempo (Piutang)</SelectItem></SelectContent>
                </Select>
              </div>
              {payment === "Tempo" && (
                <div>
                  <Label>Jatuh Tempo</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="input-due-date" />
                </div>
              )}
              <div>
                <Label>Tanggal Transaksi {editing && <span className="text-xs text-slate-400">(tidak dapat diubah)</span>}</Label>
                <Input type="date" value={trxDate} onChange={(e) => setTrxDate(e.target.value)} disabled={!!editing} data-testid="input-trx-date" />
              </div>
            </div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 mt-2">Detail Barang (stok armada)</div>
            {items.map((it, i) => (
              <div key={it._k} className="grid grid-cols-[1fr_55px_75px_110px_85px_40px] gap-2 items-center mb-2">
                <Select value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}>
                  <SelectTrigger data-testid={`row-${i}-product`}><SelectValue placeholder="Pilih barang" /></SelectTrigger>
                  <SelectContent>{salesStock.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name} (Stok: {p.qty})</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`row-${i}-qty`} />
                <UnitSelect product={products.find(p => p.product_id === it.product_id)} value={it.unit} onChange={(v) => setItem(i, "unit", v)} testid={`row-${i}-unit`} />
                <Input type="number" placeholder="Harga" value={it.sell_price} onChange={(e) => setItem(i, "sell_price", e.target.value)} data-testid={`row-${i}-price`} />
                <Input type="number" placeholder="Diskon" value={it.discount} onChange={(e) => setItem(i, "discount", e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => removeItem(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addItem} data-testid="btn-add-row"><Plus className="h-3 w-3 mr-1" />Baris</Button>
            <DialogFooter className="items-center">
              <div className="mr-auto text-sm">Total: <span className="font-mono font-bold">{rupiah(total)}</span></div>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-invoice">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <InvoicePrint invoice={printInv} onClose={() => setPrintInv(null)} />
        <PaymentDialog invoice={payInv} onClose={() => setPayInv(null)} onDone={load} />
      </div>
      <Card className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 items-end" data-testid="invoice-filters">
        <div className="col-span-2"><Label className="text-xs">Cari No. Invoice / Customer</Label><Input placeholder="mis. INV-2026… atau Toko…" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} data-testid="filter-invoice-q" /></div>
        <div><Label className="text-xs">Dari</Label><Input type="date" value={flt.from} onChange={(e) => setFlt({ ...flt, from: e.target.value })} data-testid="filter-invoice-from" /></div>
        <div><Label className="text-xs">Sampai</Label><Input type="date" value={flt.to} onChange={(e) => setFlt({ ...flt, to: e.target.value })} data-testid="filter-invoice-to" /></div>
        <div><Label className="text-xs">Sales</Label>
          <Select value={flt.sales || "all"} onValueChange={(v) => setFlt({ ...flt, sales: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-invoice-sales"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status Bayar</Label>
          <Select value={flt.pay || "all"} onValueChange={(v) => setFlt({ ...flt, pay: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-invoice-pay"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="lunas">Lunas</SelectItem><SelectItem value="sebagian">Sebagian (Cicilan)</SelectItem><SelectItem value="belum">Belum Dibayar</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Metode</Label>
          <Select value={flt.method || "all"} onValueChange={(v) => setFlt({ ...flt, method: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-invoice-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="Tunai">Tunai</SelectItem><SelectItem value="Transfer">Transfer</SelectItem><SelectItem value="Tempo">Tempo</SelectItem></SelectContent>
          </Select>
        </div>
      </Card>
      <Card>
        <div className="px-4 pt-3 text-xs text-slate-500" data-testid="invoice-count">Menampilkan {rows.length} invoice</div>
        <table className="w-full text-sm" data-testid="table-invoices">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="py-2 px-4">No. Invoice</th><th>Tanggal</th><th>Sales</th><th>Customer</th><th>Bayar</th><th>Jatuh Tempo</th><th>Status</th><th className="text-right">Total</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.invoice_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(r.date)}</td><td>{r.sales_name}</td><td>{r.customer}{r.price_code && <Badge className="ml-1 bg-indigo-100 text-indigo-800 font-mono text-[10px] font-normal">{r.price_code}</Badge>}</td><td>{r.payment_method}</td>
                <td className="font-mono text-xs">{r.due_date ? formatDate(r.due_date) : "-"}</td>
                <td>{r.is_paid
                  ? <Badge className="bg-emerald-100 text-emerald-800 font-normal" data-testid={`paid-${r.trx_no}`}>Lunas</Badge>
                  : (r.paid_amount > 0
                    ? <Badge className="bg-amber-100 text-amber-800 font-normal" data-testid={`partial-${r.trx_no}`}>Sebagian</Badge>
                    : <Badge className="bg-rose-100 text-rose-800 font-normal" data-testid={`unpaid-${r.trx_no}`}>Belum Lunas</Badge>)}</td>
                <td className="text-right font-mono">{rupiah(r.total)}</td>
                <td className="whitespace-nowrap">
                  {r.payment_method === "Tempo" && !r.is_paid && <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => setPayInv(r)} data-testid={`pay-${r.trx_no}`}>Cicil</Button>}
                  <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => openPrint(r)} data-testid={`print-${r.trx_no}`}><Printer className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => openEdit(r)} data-testid={`edit-${r.trx_no}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => deleteInvoice(r)} data-testid={`delete-${r.trx_no}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Belum ada penjualan</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
